import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery } from './baseQuery';
import type {
  IIntegration,
  IIntegrationMapping,
  IIntegrationMappingScope,
  IIntegrationWithMappings,
} from '../integrations.types';

interface IGetIntegrationMappingsParams {
  integrationUuid: string;
  scope: IIntegrationMappingScope;
}

export const integrationsApi = createApi({
  reducerPath: 'integrationsApi',
  baseQuery: createBaseQuery(),
  tagTypes: ['Integration'],
  endpoints: (builder) => ({
    getIntegrationMappings: builder.query<IIntegrationMapping[], IGetIntegrationMappingsParams>({
      query: ({ integrationUuid, scope }) => ({
        url: `/integrations/${integrationUuid}/mappings`,
        params: {
          scope,
        },
      }),
      transformResponse: (response: { data: IIntegrationMapping[] }) => {
        return response.data;
      },
      providesTags: ['Integration'],
    }),
    getIntegrations: builder.query<IIntegration[], void>({
      query: () => ({
        url: `/integrations`,
      }),
      transformResponse: (response: { data: IIntegration[] }) => {
        return response.data;
      },
      providesTags: ['Integration'],
    }),
    getIntegrationsWithMappings: builder.query<
      (IIntegration & { mappings: IIntegrationMapping[] })[],
      IIntegrationMappingScope
    >({
      async queryFn(
        scope,
        { dispatch },
      ): Promise<{ data: IIntegrationWithMappings[] } | { error: { status: string; error: string } }> {
        try {
          // Use the existing endpoints internally
          const integrationsResult = await dispatch(integrationsApi.endpoints.getIntegrations.initiate());

          if (integrationsResult.error) {
            return { error: integrationsResult.error };
          }

          const integrations = integrationsResult.data ?? [];

          // Fetch all mappings in parallel
          const mappingPromises = integrations.map((integration) =>
            dispatch(
              integrationsApi.endpoints.getIntegrationMappings.initiate({
                integrationUuid: integration.uuid,
                scope,
              }),
            ),
          );

          const mappingResults = await Promise.all(mappingPromises);

          // Combine the data
          const integrationsWithMappings = integrations.map((integration, index) => ({
            ...integration,
            mappings: mappingResults[index].data ?? [],
          }));

          return { data: integrationsWithMappings };
        } catch (error) {
          return { error: { status: 'CUSTOM_ERROR', error: error as string } };
        }
      },
      providesTags: ['Integration'],
    }),
  }),
});

export const { useGetIntegrationMappingsQuery, useGetIntegrationsQuery, useGetIntegrationsWithMappingsQuery } =
  integrationsApi;
